import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import { MapPin, ArrowLeft, Eye } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { profileRouterService } from "@/services/profileRouterService";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface VisitorProfile {
  id: string;
  user_id: string;
  display_name: string;
  photo_urls: string[];
  location: string;
  distance?: number;
  interaction_date?: string;
  view_count?: number;
}

const DiscoveryVisitors = () => {
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState<VisitorProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadVisitors();
    }
  }, [user]);

  const loadVisitors = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load visitors with view count (people who viewed my profile)
      const { data: visitorsData } = await supabase
        .from('profile_views')
        .select('viewer_id, viewed_at')
        .eq('viewed_id', user.id)
        .order('viewed_at', { ascending: false });

      // Group by viewer_id and count views
      const viewerCounts: { [key: string]: { count: number; lastViewed: string } } = {};
      visitorsData?.forEach(view => {
        if (!viewerCounts[view.viewer_id]) {
          viewerCounts[view.viewer_id] = { count: 0, lastViewed: view.viewed_at };
        }
        viewerCounts[view.viewer_id].count++;
        // Keep the most recent view date
        if (view.viewed_at > viewerCounts[view.viewer_id].lastViewed) {
          viewerCounts[view.viewer_id].lastViewed = view.viewed_at;
        }
      });

      const uniqueViewerIds = Object.keys(viewerCounts);
      const { data: visitorProfiles } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls, location')
        .in('user_id', uniqueViewerIds);

      const processedVisitors = uniqueViewerIds.map(viewerId => {
        const profile = visitorProfiles?.find(p => p.user_id === viewerId);
        const viewData = viewerCounts[viewerId];
        return {
          id: viewerId,
          user_id: viewerId,
          display_name: profile?.display_name || 'Anonymous',
          photo_urls: profile?.photo_urls || [],
          location: profile?.location || '',
          interaction_date: viewData.lastViewed,
          view_count: viewData.count,
          distance: Math.floor(Math.random() * 10) + 1
        };
      });

      setVisitors(processedVisitors);
    } catch (error) {
      console.error('Error loading visitors:', error);
      toast({
        title: "Error",
        description: "Failed to load visitors data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (userId: string) => {
    navigate(profileRouterService.getProfileRoute(userId));
  };

  const handleBack = () => {
    navigate('/discovery');
  };

  const ProfileCard = ({ profile }: { profile: VisitorProfile }) => {
    const timeAgo = profile.interaction_date ? new Date(profile.interaction_date).toLocaleDateString() : '';
    const isOnline = profile.interaction_date && new Date(profile.interaction_date) > new Date(Date.now() - 15 * 60 * 1000);

    return (
      <div 
        className="relative aspect-[3/4] rounded-lg overflow-hidden cursor-pointer group bg-muted/50"
        onClick={() => handleProfileClick(profile.user_id)}
      >
        {profile.photo_urls?.[0] ? (
          <img 
            src={profile.photo_urls[0]} 
            alt={profile.display_name} 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
            <div className="text-4xl font-bold text-muted-foreground">
              {profile.display_name.charAt(0)}
            </div>
          </div>
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
        
        {/* Top right indicators */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {/* Online indicator */}
          {isOnline && (
            <div className="w-3 h-3 rounded-full bg-green-500 shadow-lg border-2 border-background" />
          )}
          
          {/* View count indicator */}
          {profile.view_count && profile.view_count > 1 && (
            <div className="bg-background/70 text-foreground text-xs px-2 py-1 rounded-full font-medium border border-border">
              {profile.view_count}x
            </div>
          )}
        </div>
        
        {/* Profile info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-3 text-foreground">
          <h3 className="font-semibold text-lg leading-tight mb-1">
            {profile.display_name}
          </h3>
          
          {profile.location && (
            <div className="flex items-center text-sm text-muted-foreground mb-1">
              <MapPin className="w-3 h-3 mr-1" />
              <span className="truncate">{profile.location}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{timeAgo}</span>
            {profile.distance && (
              <span className="bg-background/50 px-2 py-1 rounded">
                {profile.distance}km
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <RightSideMenu />
        <AdPanel />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - calc(100vh / 7))' }}>
          <AlphaLoader size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background text-foreground ${isRTL ? 'rtl' : 'ltr'}`}>
      <RightSideMenu />
      <AdPanel />
      <div className="container mx-auto px-4 py-2 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button 
              onClick={handleBack}
              className="mr-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Eye className="w-6 h-6 text-neon-red mr-2" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Visitors</h1>
              <p className="text-sm text-muted-foreground">People who viewed your profile</p>
            </div>
          </div>
        </div>

        {visitors.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-muted-foreground text-lg mb-2">No visitors yet</div>
            <div className="text-muted-foreground/60 text-sm">Your profile visitors will appear here</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {visitors.map(profile => (
              <ProfileCard key={profile.id} profile={profile} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoveryVisitors;
